import { lazy, Suspense, useCallback, useDeferredValue, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { WorkOrdersProvider, useWorkOrderLayout } from '../../../context/WorkOrdersContext';
import { WorkOrderPageView } from '../../../enums/workOrderView';
import WorkOrdersTopBar from './WorkOrdersTopBar';
import WorkOrderStatusList from './WorkOrderStatusList';
import WorkOrderPanels from './WorkOrderPanels';
import SelectedWorkOrderAssetNotice from './SelectedWorkOrderAssetNotice';
import PanelSelector from './WorkOrderPanels/PanelSelector';
import { NO_FILTERS, VIEW_SEARCH_PARAM } from './constants';
import styles from './WorkOrders.module.scss';

// Called on gear hover/focus to warm the chunk; the module cache dedupes repeat calls.
const loadConfigurationSettings = () => import('../ConfigurationSettings');
const ConfigurationSettings = lazy(loadConfigurationSettings);
const WorkOrderModal = lazy(() => import('./WorkOrderModal'));

const WorkOrdersPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState('');
  const deferredSearchTerm = useDeferredValue(searchTerm);
  // Applied to the loaded orders, so the list and calendar narrow together.
  const [filters, setFilters] = useState(NO_FILTERS);
  const { activePanel, setActivePanel, isMapPicking, workOrderModal, openCreate, closeDetail } = useWorkOrderLayout();

  const isSettingsView = searchParams.get(VIEW_SEARCH_PARAM) === WorkOrderPageView.SETTINGS;

  const handleSettingsToggle = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (next.get(VIEW_SEARCH_PARAM) === WorkOrderPageView.SETTINGS) next.delete(VIEW_SEARCH_PARAM);
      else next.set(VIEW_SEARCH_PARAM, WorkOrderPageView.SETTINGS);
      return next;
    });
  }, [setSearchParams]);

  return (
    <div className={`work-orders__layout ${styles.workOrdersRoot}`}>
      <SelectedWorkOrderAssetNotice />
      <WorkOrdersTopBar
        isSettingsView={isSettingsView}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        filters={filters}
        onFiltersChange={setFilters}
        onSettingsToggle={handleSettingsToggle}
        onSettingsPreload={loadConfigurationSettings}
        onCreate={openCreate}
      />

      {/* Hidden rather than unmounted while settings is open, so list and panel state survive. */}
      <section className={styles.workOrdersBoard} hidden={isSettingsView}>
        <div className={`${styles.workOrdersContent} ${isMapPicking ? styles.workOrdersContentMapPicking : ''}`}>
          {/* Unmounted while picking on the map; its data lives in context, so remounting needs no refetch. */}
          {isMapPicking ? null : <WorkOrderStatusList searchTerm={deferredSearchTerm} filters={filters} />}
          <WorkOrderPanels activePanel={activePanel} searchTerm={deferredSearchTerm} filters={filters} isSettingsView={isSettingsView} />
        </div>
        {isMapPicking ? null : <PanelSelector activePanel={activePanel} onPanelChange={setActivePanel} />}
      </section>

      {isSettingsView ? (
        <section className={styles.workOrdersSettings}>
          <Suspense fallback={null}>
            <ConfigurationSettings />
          </Suspense>
        </section>
      ) : null}

      {/* Mounted only while a work order is open; unmounts after the close animation. */}
      {workOrderModal ? (
        <Suspense fallback={null}>
          <WorkOrderModal
            key={`${workOrderModal.mode}-${workOrderModal.id ?? 'new'}`}
            id={workOrderModal.id}
            mode={workOrderModal.mode}
            onClosed={closeDetail}
          />
        </Suspense>
      ) : null}
    </div>
  );
};

const WorkOrders = () => (
  <WorkOrdersProvider>
    <WorkOrdersPage />
  </WorkOrdersProvider>
);

export default WorkOrders;
